import { useTenant } from "@/providers/session";
import { Link, useNavigate } from "@tanstack/react-router";
import { ChevronsUpDown, Home, Slash } from "lucide-react";
import { Fragment } from "react";
import { useTranslation } from "react-i18next";

import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { ComboBox } from "@/components/ui/combo-box";
import {
  NavigationMenu,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  navigationMenuTriggerStyle,
} from "@/components/ui/navigation-menu";
import { Separator } from "@/components/ui/separator";
import { CompanyRole } from "@/lib/miscellaneous/authorization";
import { cn } from "@/lib/utils";
import type { TranslationKey } from "@/localisation/types";
import { useGetAllCompanies } from "@/queries/companies/get-all";

import { NavUser } from "./nav-user";

export default function Navbar({
  location,
  showSeparator = true,
  children,
}: {
  location: string[];
  showSeparator?: boolean;
  children?: React.ReactNode;
}) {
  const { t } = useTranslation();
  const { companySlug, role } = useTenant();
  const navigate = useNavigate();

  const { companies } = useGetAllCompanies();

  const options = companies.map((company) => ({
    label: company.companyName,
    value: company.companySlug,
    subLabel: company.owner?.name,
  }));

  return (
    <div className="w-full">
      <div className="flex flex-row gap-2 items-center pl-2 pr-4">
        <Breadcrumb className="flex-1 py-2">
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbPage>
                <ComboBox
                  variant="ghost"
                  placeholder={t("company-search")}
                  options={options}
                  disabled={role === CompanyRole.CLIENT}
                  value={companySlug}
                  triggerClassName="disabled:text-primary disabled:cursor-default disabled:opacity-100"
                  item={({ label, subLabel }) => (
                    <div className="flex flex-col">
                      <div>{label}</div>
                      <span className="text-xs text-muted-foreground">
                        {subLabel}
                      </span>
                    </div>
                  )}
                  onChange={(value) => {
                    if (value) {
                      navigate({ to: "/$company", params: { company: value } });
                    }
                  }}
                >
                  {({ label }) => (
                    <div className="flex flex-row gap-2 items-center">
                      <div className="text-lg font-semibold !text-primary">
                        {label}
                      </div>
                      {role !== CompanyRole.CLIENT && (
                        <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
                      )}
                    </div>
                  )}
                </ComboBox>
              </BreadcrumbPage>
            </BreadcrumbItem>
            <BreadcrumbSeparator>
              <Slash size={16} />
            </BreadcrumbSeparator>
            {location.filter(Boolean).map((item, index) => (
              <Fragment key={`${item}-${index}`}>
                <BreadcrumbItem>
                  <BreadcrumbPage
                    className={cn(
                      "text-lg font-semibold capitalize",
                      index === location.length - 1 && "font-bold",
                    )}
                  >
                    {t(item as TranslationKey) as string}
                  </BreadcrumbPage>
                </BreadcrumbItem>
                {index < location.length - 1 && (
                  <BreadcrumbSeparator>
                    <Slash size={16} />
                  </BreadcrumbSeparator>
                )}
              </Fragment>
            ))}
          </BreadcrumbList>
        </Breadcrumb>
        {children}
        <div className="flex flex-row gap-2 items-center">
          {role !== CompanyRole.CLIENT && (
            <NavigationMenu>
              <NavigationMenuList>
                <NavigationMenuItem>
                  <NavigationMenuLink
                    asChild
                    className={navigationMenuTriggerStyle()}
                  >
                    <Link to="/" className="flex items-center gap-1.5">
                      <Home className="h-4 w-4" />
                      {t("accounting-firm")}
                    </Link>
                  </NavigationMenuLink>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>
          )}
          <NavUser />
        </div>
      </div>
      {showSeparator && <Separator />}
    </div>
  );
}
